import React from 'react';
import { useState, useEffect, useRef } from 'react';
import axios from '../axios';
import { useParams } from 'react-router-dom';
import Header from './ComponentsOfCommercialPDF/Header';
import DeliveryChallan from './ComponentsOfCommercialPDF/DeliveryChallan';
import CommercialInvoice from './ComponentsOfCommercialPDF/CommercialInvoice';
import BeneficiaryCertificate from './ComponentsOfCommercialPDF/BeneficiaryCertificate';
import DeliveryChallanStatement from './ComponentsOfCommercialPDF/DeliveryChallanStatement';
import BillOfExchange from './ComponentsOfCommercialPDF/BillOfExchange';
import TableResizer from './ComponentsOfCommercialPDF/TableResizer';

const CommercialPDF = () => {
  const { commercialId } = useParams();
  const [table_info, set_table_info] = useState([]);
  const [commercial_info, set_commercial_info] = useState({});
  const [loading, setLoading] = useState(true);
  const [challan_info, set_challan_info] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [downloading, setDownloading] = useState(false); // NEW
  const printableRef = useRef(null);
  const [pendingSave, setPendingSave] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fontScale, setFontScale] = useState(100); // percent, 70–120
  const [rowSpacing, setRowSpacing] = useState(100); // percent, 40–100
  const [layoutResetKey, setLayoutResetKey] = useState(0); // bumped by "Reset table layout"
  // Show Group PI numbers in place of the separate PIs. Remembered per commercial on this
  // browser; on by default whenever any PI here belongs to a Group PI.
  const groupPiKey = `commercialUseGroupPi_${commercialId}`;
  const [useGroupPi, setUseGroupPi] = useState(() => {
    try {
      const stored = localStorage.getItem(groupPiKey);
      return stored === null ? true : stored === 'true';
    } catch (e) {
      return true;
    }
  });
  const toggleGroupPi = () => {
    setUseGroupPi((prev) => {
      try { localStorage.setItem(groupPiKey, String(!prev)); } catch (e) {}
      return !prev;
    });
  };

  useEffect(() => {
    if (commercialId) {
      fetch_commercial_pdf_info();
    }
  }, [commercialId]);

  const fetch_commercial_pdf_info = () => {
    Promise.all([
      axios.get(`${window.backendUrl}/get_commercial_info_for_pdf/${commercialId}`),
      axios.get(`${window.backendUrl}/get_commercial_pdf_table_info/${commercialId}`),
      axios.get(`${window.backendUrl}/get_commercial_delivery_challan_statement/${commercialId}`)
    ])
      .then((responses) => {
        set_commercial_info(responses[0].data);
        set_table_info(responses[1].data);
        set_challan_info(responses[2].data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSaveClick = () => {
    if (editMode) {
      setEditMode(false);   // drop into clean view first
      setPendingSave(true); // capture after re-render
    } else {
      captureAndSave();
    }
  };

  const captureAndSave = async () => {
    setSaving(true);
    const htmlContent = printableRef.current.outerHTML;
    try {
      await axios.post(`${window.backendUrl}/save_commercial_document/${commercialId}`, { htmlContent });
    } catch (err) {
      console.log(err);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (pendingSave && !editMode) {
      captureAndSave();
      setPendingSave(false);
    }
  }, [editMode, pendingSave]);

  const decreaseFont = () => {
    setFontScale((prev) => Math.max(70, prev - 5));
  };

  const increaseFont = () => {
    setFontScale((prev) => Math.min(120, prev + 5));
  };

  const decreaseSpacing = () => {
    setRowSpacing((prev) => Math.max(40, prev - 10));
  };

  const increaseSpacing = () => {
    setRowSpacing((prev) => Math.min(100, prev + 10));
  };

  if (loading) {
    return <></>;
  }

  // PI list for every page. With the Group PI option on, each Group PI is listed once (its own
  // number and date, where its first job was); PIs that are in no Group PI stay as they are.
  const piInfo = commercial_info.piInfo || [];
  const hasGroupPi = piInfo.some((pi) => pi.groupPiId);
  const pagePiInfo = [];
  piInfo.forEach((pi) => {
    if (!(useGroupPi && pi.groupPiId)) {
      pagePiInfo.push(pi);
    } else if (!pagePiInfo.some((p) => p.piId === `gpi-${pi.groupPiId}`)) {
      pagePiInfo.push({ piId: `gpi-${pi.groupPiId}`, piNo: pi.groupPiNo, createdDate: pi.groupPiDate });
    }
  });
  const page_commercial_info = { ...commercial_info, piInfo: pagePiInfo };

  // Row spacing (density) — deliberately separate from the font control above.
  // The font control scales the whole document with `zoom`, so it shrinks the text
  // along with the whitespace. This one leaves the text at its normal size and only
  // takes the vertical air out of the item tables (.cmp-table), which is what makes
  // a long commercial run to fewer pages while staying readable.
  //
  // At 100% nothing is emitted at all, so the default output is byte-identical to
  // before this control existed. Only vertical padding is touched — column widths
  // and horizontal padding are left alone.
  const spacingFactor = rowSpacing / 100;
  const spacingLineHeight = (1.2 + ((rowSpacing - 40) / 60) * 0.3).toFixed(2);
  const rowSpacingCss =
    rowSpacing < 100
      ? `
        .cmp-table th {
          padding-top: calc(var(--cmp-pad-th, 8px) * ${spacingFactor}) !important;
          padding-bottom: calc(var(--cmp-pad-th, 8px) * ${spacingFactor}) !important;
        }
        /* Body cells carry padding on the inner editable div, not the td itself, so
           zero the td and scale the div. The few totals cells that do pad the td
           directly just centre their text in the row instead. */
        .cmp-table td {
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }
        .cmp-table td > div,
        .cmp-table td > strong > span {
          padding-top: calc(var(--cmp-pad, 6px) * ${spacingFactor}) !important;
          padding-bottom: calc(var(--cmp-pad, 6px) * ${spacingFactor}) !important;
        }
        .cmp-table th,
        .cmp-table td {
          line-height: ${spacingLineHeight} !important;
        }
        /* Measurement cells emit a <br> after every line including the last, so each
           row carries a full blank line. Invisible cost at normal spacing, but it is
           the single biggest contributor to row height once the padding comes down. */
        .cmp-table td > div > br:last-child {
          display: none !important;
        }
      `
      : '';

  return (
    <div style={{ display: 'block', fontSize: '14px', margin: '0px 20px' }}>
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 10mm;
            @bottom-right {
              content: counter(page);
              font-size: 12px;
              color: #444;
            }
          }
          .print-page-table {
            width: 100%;
            border-collapse: collapse;
            break-before: page;
            page-break-before: always;
          }
          .print-page-table:first-of-type {
            break-before: auto;
            page-break-before: auto;
          }
          .print-page-table thead {
            display: table-header-group; /* forces repeat on every printed page */
          }
          .header-cell {
            padding-bottom: 15px;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
      <div className="no-print" style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={() => setEditMode(prev => !prev)}>
          {editMode ? 'Normal View' : 'Edit Content'}
        </button>
        <button onClick={handleSaveClick} disabled={saving}>
          {saving ? 'Saving...' : 'Save Document'}
        </button>
        {hasGroupPi && (
          <label style={{ marginLeft: '4px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
            title="Show the Group PI number on every page in place of the separate PIs">
            <input type="checkbox" checked={useGroupPi} onChange={toggleGroupPi} />
            Use Group PI No.
          </label>
        )}
        {editMode && (
          <button
            type="button"
            onClick={() => setLayoutResetKey((k) => k + 1)}
            title="Undo every column/row resize made to the tables since this page was opened"
          >
            Reset table layout
          </button>
        )}
        <span style={{ marginLeft: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <button type="button" onClick={decreaseFont} disabled={fontScale <= 70} title="Decrease font size">
            A−
          </button>
          <span style={{ minWidth: '48px', textAlign: 'center', fontSize: '13px' }}>{fontScale}%</span>
          <button type="button" onClick={increaseFont} disabled={fontScale >= 120} title="Increase font size">
            A+
          </button>
        </span>
        <span style={{ marginLeft: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '13px' }}>Row spacing</span>
          <button
            type="button"
            onClick={decreaseSpacing}
            disabled={rowSpacing <= 40}
            title="Make table rows more compact (does not change font size)"
          >
            −
          </button>
          <span style={{ minWidth: '48px', textAlign: 'center', fontSize: '13px' }}>{rowSpacing}%</span>
          <button
            type="button"
            onClick={increaseSpacing}
            disabled={rowSpacing >= 100}
            title="Make table rows taller (does not change font size)"
          >
            +
          </button>
        </span>
      </div>
      {/* Wrapper is only here to anchor the resize handles; they sit beside printableRef, not
          inside it, so they never reach the saved HTML or the print. */}
      <div style={{ position: 'relative' }}>
      <div ref={printableRef} style={{ zoom: fontScale / 100 }}>
        {/* Inside printableRef on purpose: captureAndSave() saves this div's outerHTML,
            so a style block outside it would let saved documents silently revert to
            normal spacing. */}
        {rowSpacingCss && <style>{rowSpacingCss}</style>}
        <table className="print-page-table">
          <thead>
            <tr><td className="header-cell"><Header /></td></tr>
          </thead>
          <tbody>
            <tr><td><BillOfExchange commercial_info={page_commercial_info} editMode={editMode} copyNumber={1} /></td></tr>
          </tbody>
        </table>

        <table className="print-page-table">
          <thead>
            <tr><td className="header-cell"><Header /></td></tr>
          </thead>
          <tbody>
            <tr><td><BillOfExchange commercial_info={page_commercial_info} editMode={editMode} copyNumber={2} /></td></tr>
          </tbody>
        </table>

        <table className="print-page-table">
          <thead>
            <tr><td className="header-cell"><Header /></td></tr>
          </thead>
          <tbody>
            <tr><td><DeliveryChallan commercial_info={page_commercial_info} table_info={table_info} editMode={editMode}/></td></tr>
          </tbody>
        </table>

        <table className="print-page-table">
          <thead>
            <tr><td className="header-cell"><Header /></td></tr>
          </thead>
          <tbody>
            <tr><td><CommercialInvoice commercial_info={page_commercial_info} table_info={table_info}  editMode={editMode}/></td></tr>
          </tbody>
        </table>

        <table className="print-page-table">
          <thead>
            <tr><td className="header-cell"><Header /></td></tr>
          </thead>
          <tbody>
            <tr><td><DeliveryChallan commercial_info={page_commercial_info} table_info={table_info} editMode={editMode} documentTitle="DETAILS PACKING LIST"/></td></tr>
          </tbody>
        </table>

        <table className="print-page-table">
          <thead>
            <tr><td className="header-cell"><Header /></td></tr>
          </thead>
          <tbody>
            <tr><td><DeliveryChallan commercial_info={page_commercial_info} table_info={table_info} editMode={editMode} documentTitle="TRUCK RECEIPT" /></td></tr>
          </tbody>
        </table>

        <table className="print-page-table">
          <thead>
            <tr><td className="header-cell"><Header /></td></tr>
          </thead>
          <tbody>
            <tr><td><BeneficiaryCertificate commercial_info={page_commercial_info} table_info={table_info} documentTitle="CERTIFICATE OF ORIGIN"  editMode={editMode}/></td></tr>
          </tbody>
        </table>

        <table className="print-page-table">
          <thead>
            <tr><td className="header-cell"><Header /></td></tr>
          </thead>
          <tbody>
            <tr><td><BeneficiaryCertificate commercial_info={page_commercial_info} table_info={table_info} documentTitle="BENEFICIARY CERTIFICATE" editMode={editMode} /></td></tr>
          </tbody>
        </table>

        <table className="print-page-table">
          <thead>
            <tr><td className="header-cell"><Header /></td></tr>
          </thead>
          <tbody>
            <tr><td><DeliveryChallanStatement commercial_info={page_commercial_info} challan_info={challan_info}  editMode={editMode}/></td></tr>
          </tbody>
        </table>
      </div>
      <TableResizer containerRef={printableRef} enabled={editMode} resetSignal={layoutResetKey} />
      </div>
    </div>
  );
};

export default CommercialPDF;
